// File: src/components/artistDashboard/SongEditModal.js

import React, { useState, useRef } from "react";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  doc,
  writeBatch,
  serverTimestamp 
} from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import toast from "react-hot-toast";
import { compressImage } from "../../services/fileCompression"; 
import { GENRES } from "../../config/constants";
import Select from 'react-select'; 
import { useProfile } from "../../context/ProfileContext";

const SongEditModal = ({ song, onClose, onSave }) => {
  const auth = getAuth();
  const { isDarkMode } = useProfile();

  // --- Form State Management ---
  const [form, setForm] = useState({
    title: song.title || "",
    description: song.description || "",
    genres: Array.isArray(song.genres) 
      ? song.genres.map(g => ({ value: g, label: g })) 
      : song.genre 
      ? [{ value: song.genre, label: song.genre }] 
      : [], 
    albumArtworkURL: song.albumArtworkURL || null,
    isAIGenerated: song.isAIGenerated || false,
    hasExplicitLanguage: song.hasExplicitLanguage || false,

    // Composition & Legal Metadata Fields
    composerLegalName: song.composerLegalName || "",
    lyricistLegalName: song.lyricistLegalName || "",
    productionRightsType: song.productionRightsType || "produced"
  });

  const [newArtworkFile, setNewArtworkFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const genreOptions = GENRES.map(g => ({ value: g, label: g }));
  const modalRef = useRef(null);

  const isDirty = 
    form.title !== (song.title || "") || 
    form.description !== (song.description || "") ||
    form.composerLegalName !== (song.composerLegalName || "") ||
    form.lyricistLegalName !== (song.lyricistLegalName || "") ||
    form.productionRightsType !== (song.productionRightsType || "produced") ||
    form.isAIGenerated !== (song.isAIGenerated || false) ||
    form.hasExplicitLanguage !== (song.hasExplicitLanguage || false) ||
    !!newArtworkFile;

  // React-Select theme configuration
  const reactSelectCustomStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#09090b' : '#ffffff',
      borderColor: isDarkMode ? '#27272a' : '#e4e4e7',
      borderRadius: '0.75rem',
      padding: '2px',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#ff00cc'
      }
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#18181b' : '#ffffff',
      borderRadius: '0.75rem',
      border: isDarkMode ? '1px solid #27272a' : '1px solid #e4e4e7'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#ff00cc' 
        : state.isFocused 
          ? isDarkMode ? '#27272a' : '#f4f4f5' 
          : 'transparent',
      color: state.isSelected ? '#ffffff' : isDarkMode ? '#f4f4f5' : '#18181b',
      fontSize: '0.875rem',
      fontWeight: '600'
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#27272a' : '#f4f4f5',
      borderRadius: '0.375rem'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: isDarkMode ? '#f4f4f5' : '#18181b',
      fontSize: '0.75rem',
      fontWeight: '700'
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: isDarkMode ? '#a1a1aa' : '#71717a',
      '&:hover': {
        backgroundColor: '#ff00cc',
        color: '#ffffff'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDarkMode ? '#52525b' : '#a1a1aa',
      fontSize: '0.875rem'
    })
  };

  // --- Handlers ---
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleClose = () => {
    if (isDirty) {
      const confirmClose = window.confirm("You have unsaved changes. Are you sure you want to close?");
      if (confirmClose) onClose();
    } else {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleArtworkChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size / 1024 / 1024 > 10) {
      toast.error("Artwork file must be smaller than 10MB.");
      return;
    }

    setIsCompressing(true);
    try {
      const compressedFile = await compressImage(file, { maxWidthOrHeight: 1200, maxSizeMB: 0.4 });
      setNewArtworkFile(compressedFile);
      setForm((prev) => ({ ...prev, albumArtworkURL: URL.createObjectURL(compressedFile) }));
    } catch (error) {
      toast.error("Failed to process image.");
    } finally {
      setIsCompressing(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      return toast.error("Please provide a song title.");
    }

    if (!form.composerLegalName.trim() || !form.lyricistLegalName.trim()) {
      return toast.error("Composer and Lyricist legal names are required.");
    }

    setIsSaving(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const db = getFirestore();
      const batch = writeBatch(db);
      const storage = getStorage();
      const songRef = doc(db, "songs", song.id);
      const aggregateRef = doc(db, "songAggregates", song.id);

      let finalArtworkURL = form.albumArtworkURL;

      // Upload artwork if updated
      if (newArtworkFile) {
        const fileName = `${song.id}.${newArtworkFile.name.split('.').pop()}`;
        const generateUrlResponse = await fetch("https://us-central1-atomic-musicapp.cloudfunctions.net/generateArtistUploadUrl", {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              artistId: song.artistId,
              fileName: `artwork/${fileName}`,
              contentType: newArtworkFile.type
            })
        });

        if (!generateUrlResponse.ok) throw new Error("Could not get artwork upload permission.");
        
        const { uploadUrl, finalPath } = await generateUrlResponse.json();
        await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': newArtworkFile.type }, body: newArtworkFile });
        finalArtworkURL = await getDownloadURL(ref(storage, finalPath));
      }

      // Updated fields payload (without PRO references)
      const updatedFields = {
        title: form.title.trim(),
        title_lowercase: form.title.trim().toLowerCase(),
        description: form.description.trim(),
        genres: form.genres.map(g => g.value),
        albumArtworkURL: finalArtworkURL,
        isAIGenerated: form.isAIGenerated,
        hasExplicitLanguage: form.hasExplicitLanguage,

        // Composition Details
        composerLegalName: form.composerLegalName.trim(),
        lyricistLegalName: form.lyricistLegalName.trim(),
        productionRightsType: form.productionRightsType,
      };

      batch.set(songRef, updatedFields, { merge: true });

      batch.set(aggregateRef, {
        title: form.title.trim(),
        artistName: song.artistName, 
        lastMetadataUpdate: serverTimestamp()
      }, { merge: true });

      await batch.commit();
      
      onSave({ ...song, ...updatedFields });
      toast.success("Song updated successfully!");
      onClose();

    } catch (err) {
      console.error("❌ Error saving song:", err);
      toast.error("Failed to save song updates.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };
  
  const confirmDelete = async () => {
    setIsSaving(true);
    const functions = getFunctions(undefined, "us-central1");
    const deleteSongFunction = httpsCallable(functions, "deleteSong");

    try {
      const result = await deleteSongFunction({ songId: song.id });

      if (result.data.success) {
        toast.success("Song removed from all systems.");
        onSave(null); 
        onClose();
      }
    } catch (err) {
      console.error("❌ Delete Error:", err);
      toast.error(err.message || "Failed to delete song safely.");
    } finally {
      setIsSaving(false);
      setShowDeleteConfirm(false);
    }
  };

  const inputClass = `w-full p-3 text-sm font-semibold rounded-xl border transition-all focus:outline-none focus:border-brand-pink/50 ${
    isDarkMode 
      ? "bg-zinc-950 border-zinc-800 text-white placeholder-zinc-600 shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]" 
      : "bg-white border-slate-200 text-slate-900 placeholder-slate-400 shadow-sm"
  }`;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-70 z-50 overflow-y-auto backdrop-blur-sm transition-all duration-300"
      onClick={handleBackdropClick} 
    >
      <div className="min-h-screen flex items-center justify-center p-4 pointer-events-none">
        
        <div 
          ref={modalRef}
          className={`w-full max-w-lg flex flex-col rounded-2xl shadow-2xl pointer-events-auto p-6 border transition-all duration-300 max-h-[90vh] overflow-y-auto ${
            isDarkMode 
              ? "bg-zinc-900 border-zinc-800/80 text-white shadow-black/40" 
              : "bg-white border-slate-100 text-slate-900 shadow-slate-200"
          }`}
          onClick={(e) => e.stopPropagation()} 
        >
          <h2 className="text-xl font-black uppercase tracking-tight italic mb-6 text-center">Edit Song Details</h2>
          
          <div className="space-y-4 pr-1">
            {/* Title & Description */}
            <input name="title" placeholder="Song Title" value={form.title} onChange={handleChange} className={inputClass} required />
            
            <textarea name="description" placeholder="Provide track notes or description..." value={form.description} onChange={handleChange} className={`${inputClass} min-h-[80px] resize-none leading-relaxed`} />

            {/* Artwork Input Component */}
            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-950/40 border-zinc-800/60' : 'bg-slate-50 border-slate-200'}`}>
              <label className={`text-xs font-black uppercase tracking-wider block mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>Album Artwork Asset</label>
              {isCompressing ? (
                <p className="text-xs font-black uppercase tracking-widest text-brand-pink animate-pulse py-1">Optimizing Texture Elements...</p>
              ) : (
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleArtworkChange} 
                  className={`block w-full text-xs font-bold file:mr-4 file:py-1.5 file:px-3.5 file:rounded-full file:border-0 file:text-[10px] file:font-black file:uppercase file:tracking-widest ${
                    isDarkMode 
                      ? 'text-zinc-500 file:bg-zinc-800 file:text-zinc-300' 
                      : 'text-gray-500 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100'
                  } mt-1`}
                />
              )}
              {form.albumArtworkURL && (
                <img src={form.albumArtworkURL} alt="Artwork Preview" className={`w-20 h-20 rounded-xl object-cover mt-4 border shadow-sm ${isDarkMode ? 'border-zinc-800' : 'border-slate-200'}`} />
              )}
            </div>

            {/* Composition Metadata Layer (Composer & Lyricist) */}
            <div className={`p-4 rounded-xl border space-y-3 ${isDarkMode ? 'bg-zinc-950/40 border-zinc-800/60' : 'bg-slate-50 border-slate-200'}`}>
              <label className="block text-xs font-black uppercase tracking-wider text-[#ff00cc]">Work Composition (Legal Names)</label>
              <input 
                type="text" 
                name="composerLegalName"
                placeholder="Composer (Full Legal Name)" 
                value={form.composerLegalName} 
                onChange={handleChange} 
                className={inputClass} 
                required 
              />
              <input 
                type="text" 
                name="lyricistLegalName"
                placeholder="Lyricist (Full Legal Name)" 
                value={form.lyricistLegalName} 
                onChange={handleChange} 
                className={inputClass} 
                required 
              />
            </div>

            {/* Production Rights Metadata Layer */}
            <div className={`p-4 rounded-xl border space-y-3 ${isDarkMode ? 'bg-zinc-950/40 border-zinc-800/60' : 'bg-slate-50 border-slate-200'}`}>
              <label className="block text-xs font-black uppercase tracking-wider text-[#ff00cc]">Production Rights</label>
              <div className="flex gap-2">
                <button 
                  type="button" 
                  onClick={() => setForm(prev => ({ ...prev, productionRightsType: "produced" }))} 
                  className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-all ${form.productionRightsType === 'produced' ? 'bg-brand-pink text-white border-transparent' : 'bg-transparent border-zinc-700 text-gray-400'}`}
                >
                  I produced this beat
                </button>
                <button 
                  type="button" 
                  onClick={() => setForm(prev => ({ ...prev, productionRightsType: "licensed" }))} 
                  className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-all ${form.productionRightsType === 'licensed' ? 'bg-brand-pink text-white border-transparent' : 'bg-transparent border-zinc-700 text-gray-400'}`}
                >
                  I have a License
                </button>
              </div>
            </div>

            {/* Genre Select Component */}
            <Select
              isMulti
              name="genres"
              options={genreOptions}
              value={form.genres}
              styles={reactSelectCustomStyles}
              onChange={(selectedOptions) => setForm(prev => ({ ...prev, genres: selectedOptions || [] }))}
              className="w-full text-xs font-semibold"
              placeholder="Select genres..."
            />

            {/* Content Attribute Checkboxes */}
            <div className={`flex items-center p-3 rounded-xl border ${isDarkMode ? 'bg-zinc-950/20 border-zinc-800/60' : 'bg-white border-slate-200'}`}>
              <input 
                  type="checkbox" 
                  id="hasExplicitLanguage" 
                  name="hasExplicitLanguage" 
                  checked={form.hasExplicitLanguage} 
                  onChange={handleChange} 
                  className="mr-3 h-4 w-4 rounded text-brand-pink accent-[#ff00cc] border-zinc-300 dark:border-zinc-700 bg-transparent cursor-pointer focus:ring-0"
              />
              <label htmlFor="hasExplicitLanguage" className={`text-xs font-bold uppercase tracking-tight cursor-pointer ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
                  This song contains explicit language.
              </label>
            </div>

            <div className={`flex items-center p-3 rounded-xl border ${isDarkMode ? 'bg-zinc-950/20 border-zinc-800/60' : 'bg-white border-slate-200'}`}>
              <input 
                  type="checkbox" 
                  id="aiGeneratedCheck" 
                  name="isAIGenerated" 
                  checked={form.isAIGenerated} 
                  onChange={handleChange} 
                  className="mr-3 h-4 w-4 rounded text-brand-pink accent-[#ff00cc] border-zinc-300 dark:border-zinc-700 bg-transparent cursor-pointer focus:ring-0"
              />
              <label htmlFor="aiGeneratedCheck" className={`text-xs font-bold uppercase tracking-tight cursor-pointer ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
                  Audio track was wholly generated by AI.
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className={`flex justify-between items-center mt-6 pt-4 border-t ${isDarkMode ? 'border-zinc-800' : 'border-slate-100'}`}>
            <button onClick={handleDelete} className="text-red-500 hover:text-red-400 text-xs font-black uppercase tracking-widest px-2 py-2 rounded-lg transition-colors" disabled={isSaving}>
              Delete Song
            </button>
            
            <div className="flex items-center space-x-2">
              <button 
                type="button" 
                onClick={handleClose} 
                className={`px-4 py-2 text-xs font-black uppercase tracking-widest rounded-xl transition-colors focus:outline-none ${
                    isDarkMode ? 'text-zinc-400 hover:bg-zinc-800 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-slate-800'
                }`}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmit} 
                className={`text-xs font-black uppercase tracking-widest px-4 py-2.5 rounded-xl text-center shadow-md transition-all active:scale-98 ${
                    isDarkMode 
                      ? "bg-brand-pink text-white hover:brightness-110 shadow-brand-pink/10" 
                      : "bg-blue-600 hover:bg-blue-700 text-white shadow-blue-100"
                }`}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100] backdrop-blur-sm">
          <div className={`rounded-2xl p-6 shadow-2xl text-center max-w-sm border transition-all duration-300 mx-4 ${
              isDarkMode ? 'bg-zinc-900 border-zinc-800 text-white' : 'bg-white border-slate-100 text-slate-900'
          }`}>
            <h3 className="text-base font-black uppercase tracking-tight italic mb-2">Delete Track Catalogue Entry?</h3>
            <p className={`text-xs font-semibold leading-relaxed mb-6 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
              This will safely remove the song from all global pipelines. Are you sure you want to continue?
            </p>
            <div className="flex justify-center space-x-2 text-xs font-black uppercase tracking-widest">
              <button 
                onClick={() => setShowDeleteConfirm(false)} 
                className={`px-5 py-2 rounded-xl transition-colors ${
                    isDarkMode ? 'text-zinc-400 hover:bg-zinc-800' : 'text-gray-500 hover:bg-gray-100'
                }`}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete} 
                className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl shadow-md shadow-red-600/10 active:scale-98 transition-all"
                disabled={isSaving}
              >
                {isSaving ? "Purging..." : "Yes, Purge Track"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SongEditModal;